import { useQueries, useQuery } from "@tanstack/react-query";
import { Api } from "../../../services/interceptor";
import { QUERY_KEY, getLastNoversSummaryLatestParams, getLastNoversSummaryOlderQueryParams } from "../../../services/queryEndPoint";
import type { MatchProgression } from "./derive";
import type {
  IMatchCommentary,
  IMatchDetails,
  InningScorecard,
  IPlayersTeamSquad,
} from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";

/** Safety cap on commentary pages per innings (5 overs each, so 40 pages = 200 overs). */
const MAX_COMMENTARY_PAGES = 40;

const fetchAllOvers = async (inningId: string): Promise<IMatchCommentary[]> => {
  const byOver = new Map<number, IMatchCommentary>();
  let params: Record<string, unknown> = { inningId, ...getLastNoversSummaryLatestParams("FULLCOMMENTARY") };
  for (let page = 0; page < MAX_COMMENTARY_PAGES; page += 1) {
    const res = await Api.get(QUERY_KEY.getCommentary, { params });
    const list: IMatchCommentary[] = res?.data?.overSummaryList ?? [];
    if (!list.length) break;
    list.forEach((o) => {
      if (typeof o?.overNumber === "number") byOver.set(o.overNumber, o);
    });
    const minOver = Math.min(...list.map((o) => o.overNumber).filter((n) => typeof n === "number"));
    if (!Number.isFinite(minOver) || minOver < 1) break;
    params = { inningId, ...getLastNoversSummaryOlderQueryParams(minOver - 1, "FULLCOMMENTARY") };
  }
  return [...byOver.values()].sort((a, b) => a.overNumber - b.overNumber);
};

/** Bulk run-progression endpoint; resolves to null when the backend does not have it (older deployment). */
const fetchProgression = async (matchId: string): Promise<MatchProgression | null> => {
  try {
    return (await Api.get(`match/${matchId}/progression`)).data as MatchProgression;
  } catch (e) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 404 || status === 405) return null;
    throw e;
  }
};

/**
 * @param needFullCommentary true while the Commentary tab is open: ball-by-ball commentary is the expensive part,
 *   so it is only fetched on demand. If the progression endpoint is unavailable it is fetched eagerly (legacy path).
 */
export const useResultMatchData = (matchId: string, needFullCommentary = false) => {
  const info = useQuery<IMatchDetails>({
    queryKey: [QUERY_KEY.getMatchInfo, matchId],
    queryFn: async () => (await Api.get(`${QUERY_KEY.getMatchInfo}/${matchId}`)).data,
    enabled: !!matchId,
  });

  const scoresQ = useQuery<InningScorecard[]>({
    queryKey: [QUERY_KEY.getScoreByMatch, matchId],
    queryFn: async () => (await Api.get(`${QUERY_KEY.getScoreByMatch}/${matchId}`))?.data?.inningScoreCardList ?? [],
    enabled: !!matchId,
  });

  const scores = scoresQ.data?.length ? scoresQ.data : info.data?.inningScorecardList;
  const inningIds = (scores ?? []).map((s) => s.inningId).filter(Boolean);

  const progQ = useQuery<MatchProgression | null>({
    queryKey: ["matchProgression", matchId],
    queryFn: () => fetchProgression(matchId),
    enabled: !!matchId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const progression = progQ.data ?? null;
  // legacy fallback: no progression endpoint (or it failed) -> page the commentary like before
  const progressionUnavailable = progQ.isFetched && !progression;
  const commEnabled = needFullCommentary || progressionUnavailable;

  const commQs = useQueries({
    queries: inningIds.map((id) => ({
      queryKey: [QUERY_KEY.getCommentary, "all", matchId, id],
      queryFn: () => fetchAllOvers(id),
      staleTime: 5 * 60 * 1000,
      enabled: commEnabled,
    })),
  });

  const squadOne = useQuery<IPlayersTeamSquad>({
    queryKey: [QUERY_KEY.getTeamSquadForMatch, matchId, info.data?.teamOne?.teamSnapshotId],
    queryFn: async () => (await Api.get(`${QUERY_KEY.getTeamSquadForMatch}/${matchId}/${info.data?.teamOne?.teamSnapshotId}`)).data,
    enabled: !!matchId && !!info.data?.teamOne?.teamSnapshotId,
  });
  const squadTwo = useQuery<IPlayersTeamSquad>({
    queryKey: [QUERY_KEY.getTeamSquadForMatch, matchId, info.data?.teamTwo?.teamSnapshotId],
    queryFn: async () => (await Api.get(`${QUERY_KEY.getTeamSquadForMatch}/${matchId}/${info.data?.teamTwo?.teamSnapshotId}`)).data,
    enabled: !!matchId && !!info.data?.teamTwo?.teamSnapshotId,
  });

  return {
    info,
    scores,
    scoresLoading: scoresQ.isLoading,
    progression,
    progressionLoading: progQ.isLoading,
    commentary: commQs.map((q) => q.data),
    commentaryLoading: commQs.some((q) => q.isLoading),
    squadOne: squadOne.data,
    squadTwo: squadTwo.data,
  };
};
